
import React, { useState, useEffect } from 'react';
import { Home, User, LayoutDashboard, Calendar, Briefcase, ChevronDown, FolderOpen, History, LogOut, Keyboard, CalendarDays, Layers, Monitor, Settings, Menu, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import ProfileAvatar from '../component/ProfileAvtar';
import ConfirmationPopup from '../component/ConfirmationPopup';
import axiosInstance from '../networking/axiosconfig';

const Navbar = () => {
    const navigate = useNavigate();
    const [showLeaveMenu, setShowLeaveMenu] = useState(false);
    const [showConfigurationMenu, setShowConfigurationMenu] = useState(false);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [userName, setUserName] = useState('');
    const [userRole, setUserRole] = useState('');
    const [userRoleNumber, setUserRoleNumber] = useState(null);
    const [userImageURL, setUserImageURL] = useState(null);
    const [error, setError] = useState("");
    const [openLogoutPopup, setOpenLogoutPopup] = useState(false);

    useEffect(() => {
        // Get user data from localStorage
        const firstName = localStorage.getItem("first_name") || '';
        const lastName = localStorage.getItem("last_name") || '';
        const role = localStorage.getItem("role");
        const profileImg = localStorage.getItem("user_profile_img") || '';
        const userId = localStorage.getItem("userId");

        // Combine first and last name
        const fullName = `${firstName} ${lastName}`.trim();
        setUserName(fullName);
        setUserImageURL(profileImg);

        // Store role number for access control
        const roleNum = role ? Number(role) : null;
        setUserRoleNumber(roleNum);

        // Map role number to role name
        const roleMap = {
            '1': 'Owner/Pivot',
            '2': 'Manager',
            '3': 'Employee'
        };
        setUserRole(roleMap[role] || 'Unknown');
        
        if (userId) {
            getUserDetail(userId);
        }

        // Listen for profile image updates
        const handleProfileImageUpdate = () => {
            const userId = localStorage.getItem("userId");
            if (userId) {
                getUserDetail(userId);
            }
        };

        window.addEventListener('profileImageUpdated', handleProfileImageUpdate);

        return () => {
            window.removeEventListener('profileImageUpdated', handleProfileImageUpdate);
        };
    }, []);

    const handleLogout = () => {
        // Clear all authentication data from localStorage
        localStorage.removeItem("token");
        localStorage.removeItem("userId");
        localStorage.removeItem("first_name");
        localStorage.removeItem("last_name");
        localStorage.removeItem("role");
        localStorage.removeItem("user_profile_img");

        // Clear sessionStorage if needed
        sessionStorage.clear();

        // Redirect to login page
        navigate("/", { replace: true });
    };

    function getUserDetail(userId) {
        axiosInstance
            .get(`/api/users/${userId}`)
            .then((res) => {
                const img = res.data.data.user_profile_img;

                // Handle image URL - if it's already a full URL, use it; otherwise construct it
                let imageUrl = img;
                if (img && !img.startsWith('http')) {
                    imageUrl = `http://attendance.inurum.com${img.startsWith('/') ? '' : '/'}${img}`;
                }

                // ✅ cache busting to force refresh
                const updatedImg = imageUrl ? `${imageUrl}?t=${Date.now()}` : null;

                setUserImageURL(updatedImg);

                // Sync localStorage
                if (imageUrl) {
                    localStorage.setItem("user_profile_img", imageUrl);
                }
            })
            .catch((err) => {
                setError(err.message);
            });
    }

    // Navigation items based on user role
    const getNavItems = () => {
        const items = [];

        // Dashboard and Home for Owner/Pivot and Manager
        if (userRoleNumber === 1 || userRoleNumber === 2) {
            items.push({
                id: 'dashboard',
                label: 'Dashboard',
                icon: <LayoutDashboard size={20} />,
                onClick: () => navigate('/dashboard'),
                visible: true
            });
            items.push({
                id: 'calendar',
                label: 'Calendar',
                icon: <Calendar size={20} />,
                onClick: () => navigate('/calendar'),
                visible: true
            });
        }

        // My Profile for Employees
        if (userRoleNumber === 3) {
            items.push({
                id: 'my-profile',
                label: 'My Profile',
                icon: <User size={20} />,
                onClick: () => navigate('/my-profile'),
                visible: true
            });
        }

        // Web Checking for all
        items.push({
            id: 'web-checking',
            label: 'Web Checking',
            icon: <User size={20} />,
            onClick: () => navigate('/Web-Checking'),
            visible: true
        });

        // My Attendance for Employees
        if (userRoleNumber === 3) {
            items.push({
                id: 'employee-attendance',
                label: 'My Attendance',
                icon: <Calendar size={20} />,
                onClick: () => navigate('/employee-attendance'),
                visible: true
            });
        }

        // Leave for Employee
        if (userRoleNumber === 3) {
            items.push({
                id: 'employee-leave',
                label: 'Leave',
                icon: <Briefcase size={20} />,
                onClick: () => navigate('/employee/leave'),
                visible: true
            });
        }

        // Manage Employees for Owner/Pivot and Manager
        if (userRoleNumber === 1 || userRoleNumber === 2) {
            items.push({
                id: 'manage-employees',
                label: 'Manage Employee',
                icon: <User size={20} />,
                onClick: () => navigate('/manage-employees'),
                visible: true
            });
        }

        // Holiday Management for all
        items.push({
            id: 'holiday-management',
            label: 'Holidays',
            icon: <Calendar size={20} />,
            onClick: () => navigate('/holiday-management'),
            visible: true
        });

        return items;
    };

    const navItems = getNavItems();

    return (
        <nav className="bg-white shadow-md border-b border-gray-200 sticky top-0 z-50">
            <div className="max-w-8xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex items-center justify-between h-16">
                    {/* Logo/Brand */}
                    <div className="flex items-center gap-2">
                        <img 
                            src='http://attendance.inurum.com/uploads/1758720302618-312666426.png'
                            alt='Inurum Payroll'
                            className='w-32 h-10 sm:w-40 sm:h-13'
                        />
                    </div>

                    {/* Desktop Navigation */}
                    <div className="hidden lg:flex items-center gap-4">
                        {/* Main Navigation Items */}
                        <div className="flex items-center gap-2">
                            {navItems.map((item) => (
                                item.visible && (
                                    <button
                                        key={item.id}
                                        onClick={item.onClick}
                                        className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-blue-50 transition-colors text-gray-700 hover:text-blue-600 whitespace-nowrap"
                                        title={item.label}
                                    >
                                        {item.icon}
                                        <span>{item.label}</span>
                                    </button>
                                )
                            ))}
                        </div>

                        {/* Leave Dropdown Menu - Only for Owner/Pivot (1) and Manager (2) */}
                        {(userRoleNumber === 1 || userRoleNumber === 2) && (
                            <div className="relative">
                                <button
                                    onClick={() => setShowLeaveMenu(!showLeaveMenu)}
                                    className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-blue-50 transition-colors text-gray-700 hover:text-blue-600 whitespace-nowrap"
                                    title="Leave Management"
                                >
                                    <Briefcase size={20} />
                                    <span>Leave</span>
                                    <ChevronDown size={16} className={`transition-transform ${showLeaveMenu ? 'rotate-180' : ''}`} />
                                </button>

                                {/* Dropdown Menu */}
                                {showLeaveMenu && (
                                    <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50">
                                        <button
                                            onClick={() => {
                                                navigate('/leave/current-leave');
                                                setShowLeaveMenu(false);
                                            }}
                                            className="w-full flex items-center gap-2 px-4 py-2 text-left hover:bg-blue-50 transition-colors text-gray-700 hover:text-blue-600"
                                        >
                                            <FolderOpen size={18} />
                                            <span>Current Leave</span>
                                        </button>
                                        <button
                                            onClick={() => {
                                                navigate('/leave/history-leave');
                                                setShowLeaveMenu(false);
                                            }}
                                            className="w-full flex items-center gap-2 px-4 py-2 text-left hover:bg-blue-50 transition-colors text-gray-700 hover:text-blue-600"
                                        >
                                            <History size={18} />
                                            <span>History Leave</span>
                                        </button>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Configuration Menu - Only for Owner/Pivot (1) and Manager (2) */}
                        {(userRoleNumber === 1 || userRoleNumber === 2) && (
                            <div className="relative">
                                <button
                                    onClick={() => setShowConfigurationMenu(!showConfigurationMenu)}
                                    className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-blue-50 transition-colors text-gray-700 hover:text-blue-600 whitespace-nowrap"
                                    title="Configuration"
                                >
                                    <Keyboard size={20} />
                                    <span>Configuration</span>
                                    <ChevronDown size={16} className={`transition-transform ${showConfigurationMenu ? 'rotate-180' : ''}`} />
                                </button>

                                {/* Dropdown Menu */}
                                {showConfigurationMenu && (
                                    <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50">
                                        <button
                                            onClick={() => {
                                                navigate('/master/leave');
                                                setShowConfigurationMenu(false);
                                            }}
                                            className="w-full flex items-center gap-2 px-4 py-2 text-left hover:bg-blue-50 transition-colors text-gray-700 hover:text-blue-600"
                                        >
                                            <CalendarDays size={18} />
                                            <span>Leave Type</span>
                                        </button>
                                        <button
                                            onClick={() => {
                                                navigate('/department');
                                                setShowConfigurationMenu(false);
                                            }}
                                            className="w-full flex items-center gap-2 px-4 py-2 text-left hover:bg-blue-50 transition-colors text-gray-700 hover:text-blue-600"
                                        >
                                            <Layers size={18} />
                                            <span>Department</span>
                                        </button>
                                        <button
                                            onClick={() => {
                                                navigate('/designation');
                                                setShowConfigurationMenu(false);
                                            }}
                                            className="w-full flex items-center gap-2 px-4 py-2 text-left hover:bg-blue-50 transition-colors text-gray-700 hover:text-blue-600"
                                        >
                                            <Monitor size={18} />
                                            <span>Designation</span>
                                        </button>
                                        <button
                                            onClick={() => {
                                                navigate('/genralSetting');
                                                setShowConfigurationMenu(false);
                                            }}
                                            className="w-full flex items-center gap-2 px-4 py-2 text-left hover:bg-blue-50 transition-colors text-gray-700 hover:text-blue-600"
                                        >
                                            <Settings size={18} />
                                            <span>Settings</span>
                                        </button>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* User Info */}
                        <div className="flex items-center gap-3 px-3 py-2 border-l border-gray-200">
                            <ProfileAvatar
                                url={userImageURL}
                                name={userName}
                                className="h-10 w-10"
                                key={userImageURL}
                            />
                            <div className="flex flex-col">
                                <span className="text-sm font-semibold text-gray-900">{userName || 'User'}</span>
                                <span className="text-xs text-gray-500">{userRole}</span>
                            </div>
                        </div>

                        {/* Logout Button */}
                        <button
                            onClick={() => setOpenLogoutPopup(true)}
                            className="flex items-center gap-2 px-4 py-2 rounded-lg hover:bg-red-50 transition-colors text-gray-700 hover:text-red-600 border border-gray-300 hover:border-red-300 whitespace-nowrap"
                            title="Logout"
                        >
                            <LogOut size={20} />
                            <span>Logout</span>
                        </button>
                    </div>

                    {/* Mobile menu button */}
                    <div className="lg:hidden flex items-center gap-4">
                        <button
                            onClick={() => setOpenLogoutPopup(true)}
                            className="p-2 rounded-lg hover:bg-red-50 transition-colors text-gray-700 hover:text-red-600"
                            title="Logout"
                        >
                            <LogOut size={20} />
                        </button>
                        <button
                            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                            className="p-2 rounded-lg hover:bg-gray-100 transition-colors text-gray-700"
                        >
                            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
                        </button>
                    </div>
                </div>
            </div>

            {/* Mobile Navigation Menu */}
            {mobileMenuOpen && (
                <div className="lg:hidden bg-white border-t border-gray-200 shadow-lg">
                    <div className="px-4 py-3 space-y-1">
                        {/* User Info Mobile */}
                        <div className="flex items-center gap-3 px-3 py-3 mb-4 bg-gray-50 rounded-lg">
                            <ProfileAvatar
                                url={userImageURL}
                                name={userName}
                                className="h-12 w-12"
                                key={userImageURL}
                            />
                            <div className="flex flex-col">
                                <span className="text-sm font-semibold text-gray-900">{userName || 'User'}</span>
                                <span className="text-xs text-gray-500">{userRole}</span>
                            </div>
                        </div>

                        {/* Main Navigation Items Mobile */}
                        {navItems.map((item) => (
                            item.visible && (
                                <button
                                    key={item.id}
                                    onClick={() => {
                                        item.onClick();
                                        setMobileMenuOpen(false);
                                    }}
                                    className="w-full flex items-center gap-3 px-3 py-3 rounded-lg hover:bg-blue-50 transition-colors text-gray-700 hover:text-blue-600"
                                >
                                    {item.icon}
                                    <span>{item.label}</span>
                                </button>
                            )
                        ))}

                        {/* Leave Menu Mobile */}
                        {(userRoleNumber === 1 || userRoleNumber === 2) && (
                            <div className="space-y-1">
                                <div className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                    Leave Management
                                </div>
                                <button
                                    onClick={() => {
                                        navigate('/leave/current-leave');
                                        setMobileMenuOpen(false);
                                    }}
                                    className="w-full flex items-center gap-3 px-3 py-3 rounded-lg hover:bg-blue-50 transition-colors text-gray-700 hover:text-blue-600"
                                >
                                    <FolderOpen size={18} />
                                    <span>Current Leave</span>
                                </button>
                                <button
                                    onClick={() => {
                                        navigate('/leave/history-leave');
                                        setMobileMenuOpen(false);
                                    }}
                                    className="w-full flex items-center gap-3 px-3 py-3 rounded-lg hover:bg-blue-50 transition-colors text-gray-700 hover:text-blue-600"
                                >
                                    <History size={18} />
                                    <span>History Leave</span>
                                </button>
                            </div>
                        )}

                        {/* Configuration Menu Mobile */}
                        {(userRoleNumber === 1 || userRoleNumber === 2) && (
                            <div className="space-y-1">
                                <div className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                    Configuration
                                </div>
                                <button
                                    onClick={() => {
                                        navigate('/master/leave');
                                        setMobileMenuOpen(false);
                                    }}
                                    className="w-full flex items-center gap-3 px-3 py-3 rounded-lg hover:bg-blue-50 transition-colors text-gray-700 hover:text-blue-600"
                                >
                                    <CalendarDays size={18} />
                                    <span>Leave Type</span>
                                </button>
                                <button
                                    onClick={() => {
                                        navigate('/department');
                                        setMobileMenuOpen(false);
                                    }}
                                    className="w-full flex items-center gap-3 px-3 py-3 rounded-lg hover:bg-blue-50 transition-colors text-gray-700 hover:text-blue-600"
                                >
                                    <Layers size={18} />
                                    <span>Department</span>
                                </button>
                                <button
                                    onClick={() => {
                                        navigate('/designation');
                                        setMobileMenuOpen(false);
                                    }}
                                    className="w-full flex items-center gap-3 px-3 py-3 rounded-lg hover:bg-blue-50 transition-colors text-gray-700 hover:text-blue-600"
                                >
                                    <Monitor size={18} />
                                    <span>Designation</span>
                                </button>
                                <button
                                    onClick={() => {
                                        navigate('/settings');
                                        setMobileMenuOpen(false);
                                    }}
                                    className="w-full flex items-center gap-3 px-3 py-3 rounded-lg hover:bg-blue-50 transition-colors text-gray-700 hover:text-blue-600"
                                >
                                    <Settings size={18} />
                                    <span>Settings</span>
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Overlay to close dropdowns when clicking outside */}
            {(showLeaveMenu || showConfigurationMenu || mobileMenuOpen) && (
                <div
                    className="fixed inset-0 z-40 bg-black bg-opacity-25"
                    onClick={() => {
                        setShowLeaveMenu(false);
                        setShowConfigurationMenu(false);
                        setMobileMenuOpen(false);
                    }}
                />
            )}

            {/* Logout Confirmation Popup */}
            <ConfirmationPopup
                open={openLogoutPopup}
                setOpen={setOpenLogoutPopup}
                title="Confirm Logout"
                message="Are you sure you want to logout?"
                confirmText="Logout"
                onConfirm={() => {
                    handleLogout();
                    setOpenLogoutPopup(false);
                }}
            />
        </nav>
    );
};

export default Navbar;
